import Bus from '@client/events/bus';
import {Logger, Category} from '@simpleinvest/front-end-logger';
import Context from '@client/events/context';
// eslint-disable-next-line import/no-cycle
import transferService from '@client/services/transfers';
import {getMsApiGatewayHostname} from '@simpleinvest/ms-core-sdk';
import {removeQueryParam} from '@client/util/router/helpers';
import {OAUTH_STATE_ID} from '@client/constants/plaid';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let Plaid: any;

const logger = new Logger({
  category: Category.TransferMoney,
  fileName: 'plaidCreateHelper.ts',
});

interface OnExitOptions {
  functionName: string;
  commit?: (mutation: string, value: boolean) => void;
  error: Record<string, unknown>;
  metadata: Record<string, unknown>;
}

interface TriggerPlaidOptions {
  clUserId: number;
  clientName: string;
  itemId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventPayload?: any;
}

interface PlaidResponse {
  complete: boolean;
  publicToken?: string;
  accountId?: string;
  // Only available after updating an item.
  itemId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventPayload?: any;
}

const toThrowable = (error): Error =>
  error instanceof Error ? error : new Error(JSON.stringify(error.error_message || error.message || error));

export const logMetadata = (functionName: string, metadata: Record<string, unknown>): void => {
  logger.addBreadcrumb({
    callingFuncName: functionName,
    message: `Plaid Metadata: ${JSON.stringify(metadata)}`,
  });
};

export const onEvent = (eventName: string, metadata: Record<string, unknown>): void => {
  logMetadata('initializePlaid', {...metadata, eventName});
};

export const plaidCreateOnExit = (params: OnExitOptions): void => {
  if (params.metadata.request_id === null && params.commit) {
    // reset dropdown
    params.commit('SET_PLAID_COMPLETE', false);
  }

  if (params.error) {
    logMetadata(params.functionName, params.metadata);
    const context = new Context().from('plaidCreateHelper').in('plaidCreateOnExit').with({error: params.error});

    // log specific error in case we need to show banner
    Bus.$emit(params.error.error_code, context);
    // log generic error to provide more information to devs debugging
    Bus.$emit('GENERIC_PLAID_ERROR', context);
  }
};

const cleanUpPersistence = (): void => {
  // Clean up localStorage items we needed in case of OAuth.
  window.localStorage?.removeItem('linkToken');
  window.localStorage?.removeItem('itemId');
  window.localStorage?.removeItem('eventPayload');

  if (window.location.search.includes(OAUTH_STATE_ID)) {
    // This is a bit dirty, but we need to get rid of the oauth_state_id query param.
    // There's not a great way to do this with vue-router without getting errors or pushing a new route.
    removeQueryParam(OAUTH_STATE_ID);
  }
};

const linkToken = async (options: TriggerPlaidOptions, redirectUri: string): Promise<string> => {
  const {link_token} = await transferService.createLinkToken({
    clUserId: options.clUserId,
    clientName: options.clientName,
    webhookUrl: `${getMsApiGatewayHostname()}/plaid/webhook`,
    redirectUri,
    itemId: options.itemId,
  });

  return link_token;
};

// eslint-disable-next-line max-lines-per-function
export const initializePlaid = async (options: TriggerPlaidOptions): Promise<PlaidResponse> => {
  // TODO: We could look at using sessionStorage for these, which helps in case a user closes the tab while the Plaid flow is open and comes back later.
  // TODO: Have to be careful because there are a few more constraints with sessionStorage though.
  const existingToken = window.localStorage?.getItem('linkToken');
  const existingItemId = window.localStorage?.getItem('itemId');
  const existingEventPayload = window.localStorage?.getItem('eventPayload');

  const isOAuth = window.location.search.includes(OAUTH_STATE_ID) && existingToken;
  const redirectUri = `${window.location.origin}/portal/clientdashboard/transactions`;

  const token = existingToken || (await linkToken(options, redirectUri));

  window.localStorage?.setItem('linkToken', token);
  // TODO: Make this work for micro-deposits as well.
  window.localStorage?.setItem('itemId', existingItemId || options.itemId || '');

  if (existingEventPayload || options.eventPayload) {
    window.localStorage?.setItem('eventPayload', existingEventPayload || JSON.stringify(options.eventPayload));
  }

  return new Promise((resolve, reject) => {
    const handler = Plaid.create({
      token,
      selectAccount: true,
      // eslint-disable-next-line no-undefined
      receivedRedirectUri: isOAuth ? window.location.href : undefined,
      onEvent,
      onSuccess: (publicToken, metadata) => {
        const itemId = window.localStorage?.getItem('itemId');
        let eventPayload = window.localStorage?.getItem('eventPayload');

        try {
          eventPayload = JSON.parse(eventPayload);
        } catch {
          // eventPayload may not be set, or may not be set to something we can parse, so this is fine.
          logger.addBreadcrumb({callingFuncName: 'onSuccess', message: 'Unable to parse event payload from localStorage'});
        }

        cleanUpPersistence();

        logMetadata('initializePlaid', metadata);
        resolve({complete: true, publicToken, accountId: metadata.account_id, itemId, eventPayload});
      },
      onExit: (error, metadata) => {
        cleanUpPersistence();

        plaidCreateOnExit({functionName: 'initializePlaid', error, metadata});

        if (error) {
          // TODO: If we were in update mode and no update is required (error_code === 'item-no-error'), can we handle that and PATCH that request?
          // TODO: Handle errors from Plaid.create if the link token is expired.
          reject(toThrowable(error));
        } else {
          resolve({complete: false});
        }
      },
    });

    handler.open();
  });
};
